import React, { useEffect, useState, useMemo } from "react";
import { Tag, Tooltip } from "antd";
import { PatternBackground } from "../../components/PatternBackground";
import ResponsiveActionBar from "../../components/ResponsiveActionBar";
import ResponsiveTableWrapper from "../../components/ResponsiveTableWrapper";
import { exportToCSV, exportToPDF } from "../../util/util";
import dayjs from "dayjs";
import { useAuditLogs } from "../../hooks/api";

const AuditLogPage = () => {
  const [searchText, setSearchText] = useState("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  // Use React Query hook for audit logs
  const { data: auditData, isLoading } = useAuditLogs(
    pagination.current - 1, 
    pagination.pageSize
  );

  // Filter data based on search
  const filteredData = useMemo(() => {
    const logs = auditData?.content || [];
    if (!searchText) return logs;
    
    return logs.filter((item) =>
      Object.values(item).some((val) =>
        String(val).toLowerCase().includes(searchText.toLowerCase())
      )
    );
  }, [auditData, searchText]);

  // Update pagination when data changes
  useEffect(() => {
    if (auditData) {
      setPagination(prev => ({
        ...prev,
        total: auditData.totalElements || 0
      }));
    }
  }, [auditData]);

  const handleTableChange = (newPagination) => {
    setPagination(newPagination);
  };

  const handleSearch = (e) => {
    setSearchText(e.target.value);
  };

  // Helper function to render text with ellipsis after specified words
  const renderTextWithEllipsis = (text, maxWords = 5) => {
    if (!text) return "";
    
    const words = String(text).split(" ");
    if (words.length <= maxWords) {
      return text;
    }
    
    const truncatedText = words.slice(0, maxWords).join(" ") + "...";
    
    return (
      <Tooltip title={text} placement="topLeft">
        <span style={{ cursor: "help" }}>{truncatedText}</span>
      </Tooltip>
    );
  };

  // Helper function to render text with ellipsis after specified characters
  const renderTextWithCharLimit = (text, maxChars = 10) => {
    if (!text) return "";
    
    const textStr = String(text);
    if (textStr.length <= maxChars) {
      return text;
    }
    
    const truncatedText = textStr.slice(0, maxChars) + "...";
    
    return (
      <Tooltip title={text} placement="topLeft">
        <span style={{ cursor: "help" }}>{truncatedText}</span>
      </Tooltip>
    );
  };

  const columns = [
    { 
      title: "ID", 
      dataIndex: "id", 
      key: "id",
      width: 120,
      render: (text) => renderTextWithCharLimit(text, 12)
    },
    {
      title: "Created By",
      dataIndex: "createdBy",
      key: "createdBy",
      width: 120,
      onFilter: (value, record) => record.createdBy.includes(value),
      render: (text) => renderTextWithEllipsis(text)
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: 250,
      onFilter: (value, record) => record.createdBy.includes(value),
      render: (text) => renderTextWithEllipsis(text)
    },
    {
      title: "Status",
      dataIndex: "actionStatus",
      key: "actionStatus",
      width: 100,
      render: (status) => (
        <Tag color={status === "SUCCESSFUL" ? "green" : "red"}>{status}</Tag>
      ),
    },
    { 
      title: "Role", 
      dataIndex: "role", 
      key: "role",
      width: 120,
      render: (text) => renderTextWithEllipsis(text)
    },
    {
      title: "Timestamp",
      dataIndex: "timestamp",
      key: "timestamp",
      width: 180,
      render: (text) => dayjs(text).format("MMM DD, YYYY - hh:mm A"),
    },
  ];

  const exportFunction = (type) => {
    const exportData = filteredData || [];
    if (type === "pdf") {
      exportToPDF(columns, exportData, "Audit Logs");
    } else {
      exportToCSV(exportData, "Audit Logs");
    }
  };

  return (
    <div className="">
      <PatternBackground>
        <p className="lead fw-bold">Audit Logs</p>
        <small>Track all system activities and user actions</small>
      </PatternBackground>

      <div className="om-container">
        <div className="p-4 shadow-sm bg-white rounded">
          <ResponsiveActionBar
            title="System Audit Logs"
            searchValue={searchText}
            onSearchChange={handleSearch}
            onExportCSV={() => exportFunction('csv')}
            onExportPDF={() => exportFunction('pdf')}
            exportLoading={false}
          />
          
          <ResponsiveTableWrapper
            columns={columns}
            dataSource={filteredData}
            rowKey={(record) => record.id}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} logs`,
            }}
            loading={isLoading}
            onChange={handleTableChange}
          />
        </div>
      </div>
    </div>
  );
};

export default AuditLogPage;
